export interface TrainingModuleAttributes {
  name: string;
  description?: string | null;
  /// A duration summary for this Training Module, e.g. "12 min".
  durationDescription?: string | null;
  thumbnailURL?: string | null;
  campusID: string;
  categoryID: string;
}

export interface TrainingModule {
  id: string;
  churchID: string;
  attributes: TrainingModuleAttributes;
}

/// The platforms a video can be hosted on.
export type TrainingVideoPlatform = 'mux';

interface TrainingVideoBase {
  /// Uploaded by staff to our own asset domain rather than derived from Mux.
  thumbnailURL?: string | null;
}

/// Uploaded to Mux, which hasn't finished processing it into a playable asset
/// yet. A client creating a resource sends only the type.
export interface ProcessingTrainingVideo extends TrainingVideoBase {
  type: 'PROCESSING';
  uploadID?: string | null;
  assetID?: string | null;
  startedAt?: string;
}

/// A video hosted on an external platform, which must be requested from the
/// server to play.
export interface ExternalTrainingVideo extends TrainingVideoBase {
  type: 'EXTERNAL';
  platform: TrainingVideoPlatform;
  itemID: string;
}

/// A video that can be linked to directly.
export interface HostedTrainingVideo extends TrainingVideoBase {
  type: 'HOSTED';
  url: string;
}

/// The platform rejected the video, so there is nothing to play.
export interface FailedTrainingVideo extends TrainingVideoBase {
  type: 'FAILED';
  reason: string;
}

/// The video attached to a Training Resource: exactly one of still processing,
/// ready on an external platform, linked directly, or failed. The source is
/// flattened alongside thumbnailURL on the wire, discriminated by `type`.
export type TrainingVideo =
  | ProcessingTrainingVideo
  | ExternalTrainingVideo
  | HostedTrainingVideo
  | FailedTrainingVideo;

export interface TrainingResourceAttributes {
  name: string;
  description?: string | null;
  /// Markdown text for this Training Resource.
  text?: string | null;
  video?: TrainingVideo | null;
}

export interface TrainingResource {
  id: string;
  churchID: string;
  moduleID: string;
  attributes: TrainingResourceAttributes;
}

// The video-url endpoint's envelope isn't pinned down, so accept the shapes the
// rest of this API uses: a result wrapper, a bare object, or a plain string.
export function videoURLFromResponse(body: unknown): string | null {
  if (typeof body === 'string') {
    return body.length > 0 ? body : null;
  }

  if (!body || typeof body !== 'object') {
    return null;
  }

  const payload = body as Record<string, any>;
  const candidate = payload.result ?? payload;

  if (typeof candidate === 'string') {
    return candidate.length > 0 ? candidate : null;
  }

  const url = candidate?.url ?? candidate?.videoURL;

  return typeof url === 'string' && url.length > 0 ? url : null;
}

/// An assignment of a Training Module to a specific volunteer.
export interface TrainingVolunteerAssignment {
  moduleID: string;
  churchID: string;
  personID: string;
  /// The team the assignment came from. The module doesn't have to belong to
  /// that team, so this can name a team other than the one being viewed.
  volunteerTeamID: string;
  staffAssignerID?: string | null;
  notifyStaffAssignerOnCompletion: boolean;
  /// IDs of the module's resources this volunteer has finished.
  completedResourceIDs: string[];
  /// ISO 8601, or null when not recorded.
  assignedAt?: string | null;
  dueDate?: string | null;
  /// ISO 8601, or null if not completed;
  completionDate?: string | null;
}

/// An assignment paired with the module it points at. The module is absent when
/// it can't be read back -- deleted, or owned somewhere this staff member can't
/// see -- so the assignment still lists rather than disappearing.
export interface TrainingVolunteerAssignmentBundle {
  assignment: TrainingVolunteerAssignment;
  module?: TrainingModule | null;
}

// The assign endpoint hands the new assignment straight back, so it can be
// shown before the list is refetched. Read defensively: a shape we don't
// recognise just means falling back to the refetch.
export function trainingAssignmentFromResponse(body: unknown): TrainingVolunteerAssignment | null {
  if (!body || typeof body !== 'object') {
    return null;
  }

  const payload = body as Record<string, any>;
  const candidate = payload.result ?? payload;

  if (!candidate || typeof candidate !== 'object' || typeof candidate.moduleID !== 'string') {
    return null;
  }

  return {
    ...candidate,
    completedResourceIDs: Array.isArray(candidate.completedResourceIDs) ? candidate.completedResourceIDs : []
  } as TrainingVolunteerAssignment;
}
